import { CalendarIcon, Clock } from "lucide-react";
import { useState, useRef, useEffect } from "react";

// Custom Date Time Picker Component with Local Time Display
export const CustomDateTimePicker = ({
  value,
  onChange,
  error,
  label = "Start Date & Time",
}: {
  value: Date | null;
  onChange: (date: Date | null) => void;
  error?: string;
  label?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(value);
  const [displayedMonth, setDisplayedMonth] = useState(new Date().getMonth());
  const [displayedYear, setDisplayedYear] = useState(new Date().getFullYear());
  const [selectedTime, setSelectedTime] = useState(() => {
    if (value) {
      // Display in LOCAL time
      const hours = value.getHours().toString().padStart(2, "0");
      const minutes = value.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    }
    return "";
  });

  useEffect(() => {
    if (value) {
      setSelectedDate(value);
      const hours = value.getHours().toString().padStart(2, "0");
      const minutes = value.getMinutes().toString().padStart(2, "0");
      setSelectedTime(`${hours}:${minutes}`);
    } else {
      setSelectedDate(null);
      setSelectedTime("");
    }
  }, [value]);



  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get user's timezone and determine if they use 12-hour format
  const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  const handle12HourFormat = () => {
    const timezone = getUserTimezone();
    // Use 12-hour format for Africa (including Nigeria), Asia (except some countries), and Oceania
    // Use 24-hour format for Americas, Europe
    return timezone.startsWith("Africa/") ||
      timezone.startsWith("Asia/") ||
      timezone.startsWith("Pacific/") ||
      timezone.startsWith("Indian/");
  };

  const formatDisplayDate = (date: Date | null, time: string) => {
    if (!date) return "";
    // Format in LOCAL timezone
    const dateStr = date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    if (time) {
      // Convert 24-hour time to 12-hour format with AM/PM
      const [hours, minutes] = time.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${dateStr} at ${hour12}:${minutes} ${ampm}`;
    }

    return dateStr;
  };

  const handleDateSelect = (day: number, month: number, year: number) => {
    // Create date in LOCAL timezone
    const newDate = new Date(year, month, day);
    setSelectedDate(newDate);

    if (selectedTime) {
      const [hours, minutes] = selectedTime.split(":");
      newDate.setHours(parseInt(hours), parseInt(minutes));
      onChange(newDate); // This passes local Date object
    }
  };
  const handleTimeChange = (time: string) => {
    setSelectedTime(time);
    if (selectedDate) {
      const [hours, minutes] = time.split(":");
      const newDateTime = new Date(selectedDate);
      newDateTime.setHours(parseInt(hours), parseInt(minutes));
      onChange(newDateTime);
    }
  };

  const isPastTime = (slotValue: string) => {
    if (!selectedDate) return false;

    const [hours, minutes] = slotValue.split(":");
    const slotDate = new Date(selectedDate);
    slotDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    // Compare to current local time
    const now = new Date();
    return slotDate.getTime() < now.getTime();
  };


  const getMonthName = (month: number) => {
    return new Date(displayedYear, month).toLocaleDateString("en-US", {
      month: "long",
    });
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const goToPrevMonth = () => {
    if (displayedMonth === 0) {
      setDisplayedMonth(11);
      setDisplayedYear(displayedYear - 1);
    } else {
      setDisplayedMonth(displayedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (displayedMonth === 11) {
      setDisplayedMonth(0);
      setDisplayedYear(displayedYear + 1);
    } else {
      setDisplayedMonth(displayedMonth + 1);
    }
  };

  const generateTimeSlots = () => {
    const slots = [];
    const is12Hour = handle12HourFormat();

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;

        let displayTime;
        if (is12Hour) {
          // Convert to 12-hour format with AM/PM
          const ampm = hour >= 12 ? "PM" : "AM";
          const hour12 = hour % 12 || 12;
          displayTime = `${hour12}:${minute.toString().padStart(2, "0")} ${ampm}`;
        } else {
          // Keep 24-hour format
          displayTime = timeString;
        }

        slots.push({ value: timeString, display: displayTime });
      }
    }
    return slots;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(displayedYear, displayedMonth);
    const firstDay = getFirstDayOfMonth(displayedYear, displayedMonth);
    const today = new Date();

    const days = [];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Day headers
    dayNames.forEach((day) => {
      days.push(
        <div
          key={day}
          className="p-2 text-center text-xs font-medium text-gray-500"
        >
          {day}
        </div>
      );
    });

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday =
        day === today.getDate() &&
        displayedMonth === today.getMonth() &&
        displayedYear === today.getFullYear();

      const isSelected =
        selectedDate &&
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === displayedMonth &&
        selectedDate.getFullYear() === displayedYear;

      const isPast =
        new Date(displayedYear, displayedMonth, day) <
        new Date(today.setHours(0, 0, 0, 0));

      days.push(
        <button
          key={day}
          type="button"
          disabled={isPast}
          onClick={() => handleDateSelect(day, displayedMonth, displayedYear)}
          className={`
          p-2 text-sm rounded-lg transition-all duration-200
          ${isSelected
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : isPast
                ? "text-gray-300 cursor-not-allowed"
                : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
            }
          ${isToday && !isSelected ? "ring-2 ring-blue-200" : ""}
        `}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={goToPrevMonth}
            className="px-2 py-1 rounded hover:bg-gray-100"
          >
            &lt;
          </button>
          <h3 className="text-lg font-semibold text-gray-800">
            {getMonthName(displayedMonth)} {displayedYear}
          </h3>
          <button
            type="button"
            onClick={goToNextMonth}
            className="px-2 py-1 rounded hover:bg-gray-100"
          >
            &gt;
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1">{days}</div>
      </div>
    );
  };

  return (
    <div className="relative" ref={pickerRef}>
      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-4">
          {label}
        </label>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`
            w-full px-3 py-3 text-left bg-white border rounded-lg shadow-sm transition-all duration-200
            ${error
              ? "border-red-300 focus:border-red-500"
              : "border-gray-300 focus:border-blue-500"
            }
            ${isOpen
              ? "border-blue-500 ring-2 ring-blue-200"
              : "hover:border-gray-400"
            }
            focus:outline-none focus:ring-2 focus:ring-blue-200
          `}
        >
          <div className="flex items-center justify-between">
            <span className={value ? "text-gray-900" : "text-gray-500"}>
              {formatDisplayDate(selectedDate, selectedTime) ||
                "Select date & time"}
            </span>
            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-5 w-5 text-[#7B7B7B] font-semibold" />
              <Clock className="h-4 w-4 text-[#7B7B7B] font-semibold" />
            </div>
          </div>
        </button>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-50 bg-white border -mt-72 border-gray-200 rounded-xl shadow-xl">
          <div className="flex">
            {/* Calendar */}
            <div className="flex-1 border-r border-gray-100">
              {renderCalendar()}
            </div>

            {/* Time Picker */}
            {/* Time Picker */}
            <div className="w-48 p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">
                Select Time
              </h4>

              {/* Manual Time Input */}
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => handleTimeChange(e.target.value)}
                className="w-full mb-3 p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
              />

              <div className="max-h-48 overflow-y-auto space-y-1">
                {generateTimeSlots().map((slot) => {
                  const disabled = isPastTime(slot.value);
                  return (
                    <button
                      key={slot.value}
                      type="button"
                      onClick={() => !disabled && handleTimeChange(slot.value)}
                      disabled={disabled}
                      className={`
            w-full px-3 py-2 text-sm text-left rounded-md transition-colors duration-150
            ${disabled
                          ? "text-gray-300 cursor-not-allowed"
                          : selectedTime === slot.value
                            ? "bg-blue-100 text-blue-700 font-medium"
                            : "text-gray-600 hover:bg-gray-50"
                        }
          `}
                    >
                      {slot.display}
                    </button>
                  );
                })}
              </div>
            </div>


          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-100 rounded-b-xl">
            <button
              type="button"
              onClick={() => {
                setSelectedDate(null);
                setSelectedTime("");
                onChange(null);
                setIsOpen(false);
              }}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};